import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { isApiConnected } from "@/lib/apiClient";
import { listExpeditions, listNewsEvents } from "@/services/cmsService";
import { useCourses, type Course } from "@/hooks/useCourses";

interface SearchResult {
  id: string;
  type: "course" | "expedition" | "news";
  title: string;
  subtitle: string;
  href: string;
}

export default function SearchModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const { courses } = useCourses();

  const search = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const term = q.toLowerCase().trim();

    const found: SearchResult[] = [];

    // Search courses
    for (const course of courses) {
      if (
        course.name.toLowerCase().includes(term) ||
        (course.shortDescription && course.shortDescription.toLowerCase().includes(term)) ||
        (course.category && course.category.toLowerCase().includes(term))
      ) {
        found.push({
          id: course.slug,
          type: "course",
          title: course.name,
          subtitle: course.category ? `${course.category.charAt(0).toUpperCase() + course.category.slice(1)} Wing · ${course.duration}` : course.duration,
          href: `/courses/${course.slug}`,
        });
      }
    }

    // Search expeditions and news (client-side filter — the old backend has
    // no server-side text search endpoint)
    if (isApiConnected()) {
      try {
        const expeditions = await listExpeditions();
        for (const exp of expeditions) {
          if (exp.title.toLowerCase().includes(term) || exp.subtitle.toLowerCase().includes(term)) {
            found.push({
              id: exp.id,
              type: "expedition",
              title: exp.title,
              subtitle: `${exp.year || ""} · ${exp.subtitle || "Past Expedition"}`,
              href: `/past-expeditions`,
            });
            if (found.filter((r) => r.type === "expedition").length >= 5) break;
          }
        }

        const news = await listNewsEvents();
        for (const n of news) {
          if (n.title.toLowerCase().includes(term) || n.sourceName.toLowerCase().includes(term)) {
            found.push({
              id: n.id,
              type: "news",
              title: n.title,
              subtitle: n.sourceName || "News & Events",
              href: `/news-events`,
            });
            if (found.filter((r) => r.type === "news").length >= 5) break;
          }
        }
      } catch {
        // Silent fail - just show course results
      }
    }

    setResults(found);
    setLoading(false);
  }, [courses]);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setResults([]);
    }
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query, search]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const typeIcon: Record<string, string> = {
    course: "ri-road-map-line",
    expedition: "ri-flag-2-line",
    news: "ri-newspaper-line",
  };

  const typeColor: Record<string, string> = {
    course: "bg-green-100 text-green-700",
    expedition: "bg-amber-100 text-amber-700",
    news: "bg-sky-100 text-sky-700",
  };

  const typeLabel: Record<string, string> = {
    course: "Course",
    expedition: "Expedition",
    news: "News",
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl mx-4 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <i className="ri-search-line text-lg text-gray-400" />
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search courses, expeditions, news..."
            className="flex-1 text-base text-gray-800 bg-transparent outline-none placeholder:text-gray-300"
          />
          {loading && <i className="ri-loader-4-line animate-spin text-gray-300 text-lg" />}
          <button
            onClick={onClose}
            aria-label="Close search"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto">
          {query.length < 2 ? (
            <div className="px-5 py-12 text-center">
              <i className="ri-search-line text-4xl text-gray-200 block mb-3" />
              <p className="text-sm text-gray-400">Type at least 2 characters to search</p>
              <p className="text-xs text-gray-300 mt-1">Search across courses, expeditions, and news</p>
            </div>
          ) : results.length === 0 && !loading ? (
            <div className="px-5 py-12 text-center">
              <i className="ri-file-search-line text-4xl text-gray-200 block mb-3" />
              <p className="text-sm text-gray-500">No results for "{query}"</p>
              <p className="text-xs text-gray-300 mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="py-2">
              {results.map((r) => (
                <Link
                  key={`${r.type}-${r.id}`}
                  to={r.href}
                  onClick={onClose}
                  className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors">
                    <i className={`${typeIcon[r.type]} text-lg text-gray-500`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-800 truncate">{r.title}</p>
                    <p className="text-xs text-gray-400 truncate">{r.subtitle}</p>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${typeColor[r.type]}`}>
                    {typeLabel[r.type]}
                  </span>
                  <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-gray-500 transition-colors" />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-5 py-2.5 border-t border-gray-100 flex items-center gap-3 text-[11px] text-gray-300">
          <span className="flex items-center gap-1">
            <i className="ri-keyboard-box-line" />
            <span className="font-medium text-gray-400">ESC</span> to close
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-arrow-up-down-line" />
            <span className="font-medium text-gray-400">↑↓</span> to navigate
          </span>
        </div>
      </div>
    </div>
  );
}